<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { DataManager } from 'dashboard/helper/CacheHelper/DataManager';

const props = defineProps({
  collapsed: { type: Boolean, default: false },
});

const store = useStore();
const showDropdown = ref(false);
const search = ref('');

// Fase 3 · permisos · si solo tiene acceso a 1 sucursal, switcher se bloquea
const { accessibleBranchIds, isLockedToBranch, canAccessBranch } = usePermissions();

const allBranches = computed(() => store.getters['branches/getBranches'] || []);
// Filtra a las sucursales accesibles · vacío en accessibleBranchIds = todas
const branches = computed(() => {
  if (accessibleBranchIds.value.length === 0) return allBranches.value;
  return allBranches.value.filter(b => canAccessBranch(b.id));
});
const activeBranch = computed(() => store.getters['branches/getActiveBranch']);
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return branches.value;
  return branches.value.filter(
    b => b.name.toLowerCase().includes(q) || (b.code || '').toLowerCase().includes(q)
  );
});

function toggle() {
  // Si está bloqueado a 1 sucursal, no abrir dropdown
  if (isLockedToBranch.value) return;
  search.value = '';
  showDropdown.value = !showDropdown.value;
  // Refrescar al abrir: trae logo_url/portada recién subida por el admin sin
  // necesitar recargar la app (la SPA solo pedía branches/get en onMounted).
  if (showDropdown.value) store.dispatch('branches/get');
}
function close() {
  showDropdown.value = false;
}
async function select(branch) {
  if (branch.id === activeBranch.value?.id) {
    close();
    return;
  }
  // Defensa extra: no permitir selección de sucursal no accesible (guard de UI)
  if (!canAccessBranch(branch.id)) {
    close();
    return;
  }
  store.dispatch('branches/setActive', branch.id);
  close();
  const accountId = store.getters.getCurrentAccountId;
  // Bug fix 2026-05-29 (Victor): invalida caché IndexedDB de inboxes/labels al
  // cambiar branch. Sin esto, la SPA mostraba inboxes de la sucursal anterior
  // porque el cache_key del backend no cambia con X-Active-Branch-Id, y
  // CacheEnabledApiClient devolvía los datos cacheados localmente.
  try {
    const dm = new DataManager(accountId);
    await dm.initDb();
    await dm.replace({ modelName: 'inbox', data: [] });
    await dm.replace({ modelName: 'label', data: [] });
  } catch (_) { /* IDB no disponible · ignoramos */ }
  // Fix UX: redirigir EXPLÍCITAMENTE a /home (módulo Inicio · ruta home_blank) al cambiar branch.
  // window.location.href fuerza recarga completa · limpia URL + estado memoria + cualquier conv cacheada.
  // Evita que mensajes de la sucursal anterior aparezcan en la nueva.
  if (accountId) {
    window.location.href = `/app/accounts/${accountId}/home`;
  } else {
    window.location.reload();
  }
}

onMounted(() => {
  store.dispatch('branches/get');
});
</script>

<template>
  <div v-on-click-outside="close" class="branch-switcher-card-wrap">
    <!-- Modo COLAPSADO · solo avatar grande clickeable (sidebar 80px) -->
    <button
      v-if="collapsed"
      type="button"
      class="branch-avatar branch-avatar--standalone"
      :class="{ 'has-image': activeBranch?.logo_url, 'pers-style': activeBranch?.is_personal, 'is-open': showDropdown }"
      :title="activeBranch?.name || 'Cambiar sucursal'"
      @click="toggle"
    >
      <img v-if="activeBranch?.logo_url" :src="activeBranch.logo_url" :alt="activeBranch?.name" />
      <template v-else>
        <span v-if="activeBranch?.is_personal" class="av-mini-logo">VG</span>
        <template v-else>
          <span class="av-mini-logo">📷</span>
          <span class="av-code">{{ activeBranch?.code || '—' }}</span>
        </template>
      </template>
    </button>

    <!-- Modo EXPANDIDO · card completa clickeable -->
    <button
      v-else
      type="button"
      class="branch-card"
      :class="{ 'is-open': showDropdown }"
      @click="toggle"
    >
      <div
        class="branch-avatar"
        :class="{ 'has-image': activeBranch?.logo_url, 'pers-style': activeBranch?.is_personal }"
      >
        <img v-if="activeBranch?.logo_url" :src="activeBranch.logo_url" :alt="activeBranch?.name" />
        <template v-else>
          <span v-if="activeBranch?.is_personal" class="av-mini-logo">VG</span>
          <template v-else>
            <span class="av-mini-logo">📷</span>
            <span class="av-code">{{ activeBranch?.code || '—' }}</span>
          </template>
        </template>
      </div>
      <div class="branch-info">
        <div class="branch-label">Sucursal</div>
        <div class="branch-name">
          {{ activeBranch?.name?.toUpperCase() || 'SIN ASIGNAR' }}
          <span class="branch-chev" :class="{ 'is-open': showDropdown }">▾</span>
        </div>
      </div>
    </button>

    <!-- Dropdown C4 cristal negro -->
    <div v-if="showDropdown" class="branch-dropdown" :class="{ 'is-collapsed': collapsed }">
      <div class="dd-facets"></div>

      <div v-if="branches.length >= 5" class="dd-search">
        <span class="dd-search-icon">🔍</span>
        <input
          v-model="search"
          type="text"
          placeholder="Buscar sucursal…"
          @click.stop
        />
      </div>

      <div class="dd-rows">
        <button
          v-for="b in filtered"
          :key="b.id"
          type="button"
          class="dd-row"
          :class="{ active: b.id === activeBranch?.id }"
          @click="select(b)"
        >
          <div
            class="dd-avatar"
            :class="{ 'has-image': b.logo_url, 'pers-style': b.is_personal }"
          >
            <img v-if="b.logo_url" :src="b.logo_url" :alt="b.name" />
            <template v-else>
              <span v-if="b.is_personal" class="av-mini-logo">VG</span>
              <template v-else>
                <span class="av-mini-logo">📷</span>
                <span class="av-code">{{ b.code }}</span>
              </template>
            </template>
          </div>
          <div class="dd-info">
            <div class="dd-name">{{ b.name }}</div>
            <div class="dd-sub">{{ b.code }} · {{ b.is_personal ? 'Personal' : 'Sucursal' }}</div>
          </div>
          <div v-if="b.id === activeBranch?.id" class="dd-check">✓</div>
        </button>
        <div v-if="filtered.length === 0" class="dd-empty">Sin resultados</div>
      </div>

      <div class="dd-divider"></div>
      <a class="dd-action" :href="`/app/accounts/${$store.getters.getCurrentAccountId}/settings/branches/list`">
        <span class="dd-action-icon">⚙</span> Administrar sucursales
      </a>
    </div>
  </div>
</template>

<style scoped>
.branch-switcher-card-wrap { position: relative; }

/* ────── Avatar (cuadrado · siempre presente) ────── */
.branch-avatar {
  width: 50px; height: 50px; border-radius: 11px; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  font-weight: 900; font-size: 11px;
  position: relative; overflow: hidden;
  box-shadow: 0 0 0 1.5px rgba(251,191,36,0.4) inset, 0 6px 14px rgba(0,0,0,0.5);
  background:
    radial-gradient(circle at 50% 100%, rgba(251,191,36,0.5), transparent 70%),
    linear-gradient(135deg, #1a1a1a, #050505);
  color: #fbbf24;
  letter-spacing: 0.5px;
  text-align: center; line-height: 1;
  border: none;
}
.branch-avatar.has-image { background: #000; padding: 0; }
.branch-avatar img { width: 100%; height: 100%; object-fit: cover; }
.branch-avatar.pers-style {
  background: radial-gradient(circle at 50% 100%, rgba(251,191,36,0.5), transparent 70%), linear-gradient(135deg,#fbbf24 0%, #d97706 100%);
  color: #000;
}
.av-mini-logo { font-size: 18px; line-height: 1; }
.av-code { font-size: 9px; letter-spacing: 0.6px; margin-top: 1px; opacity: 0.85; }

/* Avatar standalone (collapsed): clickeable, hover scale */
.branch-avatar--standalone {
  cursor: pointer; transition: transform 0.18s, box-shadow 0.18s;
  display: flex; flex-direction: column;
  font-family: inherit;
}
.branch-avatar--standalone:hover {
  transform: scale(1.06);
  box-shadow: 0 0 0 2px rgba(251,191,36,0.7) inset, 0 0 16px rgba(251,191,36,0.35), 0 8px 20px rgba(0,0,0,0.6);
}
.branch-avatar--standalone.is-open {
  box-shadow: 0 0 0 2px rgba(251,191,36,0.85) inset, 0 0 22px rgba(251,191,36,0.50);
}

/* ────── Card expandida (no collapsed) ────── */
.branch-card {
  position: relative;
  display: flex; gap: 10px; align-items: center;
  padding: 8px 12px 8px 10px;
  background: rgba(10,10,10,0.88);
  border: 1.5px solid rgba(251,191,36,0.30);
  border-radius: 13px;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.04) inset,
    0 0 22px rgba(251,191,36,0.10),
    0 8px 24px rgba(0,0,0,0.6);
  cursor: pointer; transition: all 0.18s; overflow: hidden;
  width: 100%;
  font-family: inherit;
  text-align: left;
}
.branch-card::before {
  content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 13px;
  background:
    linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.10) 22%, transparent 24%),
    radial-gradient(circle at 100% 0%, rgba(251,191,36,0.18), transparent 60%);
  z-index: 1;
}
.branch-card > * { position: relative; z-index: 2; }
.branch-card:hover {
  transform: translateY(-2px);
  border-color: rgba(251,191,36,0.55);
  box-shadow:
    0 0 0 1px rgba(251,191,36,0.20) inset,
    0 0 32px rgba(251,191,36,0.20),
    0 14px 32px rgba(0,0,0,0.7);
}
.branch-card.is-open {
  border-color: rgba(251,191,36,0.65);
  box-shadow: 0 0 0 1px rgba(251,191,36,0.25) inset, 0 0 32px rgba(251,191,36,0.25);
}

/* Info al lado del avatar */
.branch-info { flex: 1; min-width: 0; }
.branch-label {
  font-size: 9px; letter-spacing: 1.4px; text-transform: uppercase; font-weight: 700;
  color: rgba(255,255,255,0.5);
}
.branch-name {
  font-size: 13px; font-weight: 800; color: #fff; line-height: 1.15;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
  display: flex; align-items: center; gap: 6px;
}
.branch-chev {
  font-size: 10px; color: #fbbf24; opacity: 0.85;
  transition: transform 0.18s;
}
.branch-chev.is-open { transform: rotate(180deg); }

/* ────── Dropdown C4 cristal negro ────── */
.branch-dropdown {
  position: absolute; top: calc(100% + 8px); left: 0; right: 0;
  min-width: 290px; max-width: 360px;
  background: rgba(10,10,10,0.92);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 14px;
  padding: 6px;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 0 28px rgba(255,255,255,0.08),
    0 24px 60px rgba(0,0,0,0.7);
  z-index: 50;
  overflow: hidden;
}
.branch-dropdown.is-collapsed { left: calc(100% + 8px); right: auto; min-width: 290px; top: 0; }
.dd-facets {
  position: absolute; inset: 0; pointer-events: none; border-radius: 14px; z-index: 1;
  background:
    linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.10) 22%, transparent 24%),
    linear-gradient(120deg, transparent 38%, rgba(255,255,255,0.06) 50%, transparent 52%),
    radial-gradient(circle at 100% 0%, rgba(255,255,255,0.15), transparent 65%);
}
.branch-dropdown > *:not(.dd-facets) { position: relative; z-index: 2; }

/* Search */
.dd-search {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 10px; margin-bottom: 4px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 9px;
}
.dd-search-icon { font-size: 11px; color: rgba(255,255,255,0.4); }
.dd-search input {
  flex: 1; background: transparent; border: none; outline: none;
  color: #fff; font-size: 12px;
}

/* Rows */
.dd-rows { display: flex; flex-direction: column; gap: 1px; }
.dd-row {
  display: flex; gap: 10px; align-items: center;
  padding: 8px 10px; border-radius: 10px; cursor: pointer;
  background: transparent; border: none; width: 100%;
  font-family: inherit; text-align: left;
  transition: background 0.12s;
}
.dd-row:hover { background: rgba(255,255,255,0.05); }
.dd-row.active {
  background: rgba(255,255,255,0.10);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.18);
}
.dd-avatar {
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  font-weight: 900; line-height: 1; overflow: hidden;
  box-shadow: 0 0 0 1.5px rgba(251,191,36,0.4) inset;
  background:
    radial-gradient(circle at 50% 100%, rgba(251,191,36,0.5), transparent 70%),
    linear-gradient(135deg, #1a1a1a, #050505);
  color: #fbbf24;
}
.dd-avatar.has-image { background: #000; }
.dd-avatar img { width: 100%; height: 100%; object-fit: cover; }
.dd-avatar.pers-style {
  background: radial-gradient(circle at 50% 100%, rgba(251,191,36,0.5), transparent 70%), linear-gradient(135deg,#fbbf24 0%, #d97706 100%);
  color: #000;
}
.dd-avatar .av-mini-logo { font-size: 14px; }
.dd-avatar .av-code { font-size: 8px; margin-top: 1px; }

.dd-info { flex: 1; min-width: 0; }
.dd-name {
  font-size: 12px; font-weight: 800; color: #fff; line-height: 1.2;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dd-sub {
  font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dd-check { color: #fff; font-weight: 800; font-size: 14px; }
.dd-empty { padding: 14px; font-size: 11px; color: rgba(255,255,255,0.4); text-align: center; font-style: italic; }

/* Divider + acción inferior */
.dd-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 4px 0; }
.dd-action {
  padding: 9px 10px; border-radius: 9px;
  font-size: 11px; font-weight: 700; color: rgba(251,191,36,0.85);
  display: flex; align-items: center; gap: 8px; cursor: pointer;
  text-decoration: none;
  transition: all 0.12s;
}
.dd-action:hover { background: rgba(251,191,36,0.08); color: #fbbf24; }
.dd-action-icon { font-size: 13px; }
</style>
